// frontend/src/pages/landing/PricingPage.tsx
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 *
 * Página pública de preços (Fase 3 — billing).
 * Planos vêm do BANCO via GET /api/billing/plans (endpoint público) —
 * fonte de verdade única; preços não são mais hardcoded.
 * "Começar Agora" → registro com o plano pré-selecionado.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from "@mui/material";
import LandingMenu from "../../components/layout/LandingMenu";
import PlansGrid from "../../components/billing/PlansGrid";
import { billingService, Plan, BillingCycle } from "../../services/billingService";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [ciclo, setCiclo] = useState<BillingCycle>("MONTHLY");

  useEffect(() => {
    billingService
      .listPlans()
      .then(setPlans)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (plan: Plan) => {
    // Visitante deslogado: registro com plano pré-selecionado; após o
    // onboarding, a assinatura é concluída em /owner/billing/plans
    navigate(`/register?plan=${encodeURIComponent(plan.id)}`);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LandingMenu currentPage="pricing" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Planos e Preços
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            paragraph
            align="center"
          >
            Escolha o plano ideal para o seu negócio
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <ToggleButtonGroup
              value={ciclo}
              exclusive
              size="small"
              onChange={(_e, value: BillingCycle | null) => value && setCiclo(value)}
            >
              <ToggleButton value="MONTHLY">Mensal</ToggleButton>
              <ToggleButton value="YEARLY">Anual</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {loading && (
            <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 2 }} />
          )}

          {loadError && (
            <Alert severity="warning">
              Não foi possível carregar os planos agora. Tente novamente em instantes.
            </Alert>
          )}

          {!loading && !loadError && (
            <PlansGrid
              plans={plans}
              ciclo={ciclo}
              ctaLabel="Começar Agora"
              onSelect={handleSelect}
            />
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PricingPage;
